import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Music2, Film, Mic2 } from "lucide-react";
import { useContent } from "../context/ContentContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import CinematicReel from "../components/CinematicReel";

const SERVICE_CARDS = [
  {
    label: "Audio Branding",
    why: "Awareness · memorabilidad · consistencia · conexión emocional · capital de marca",
    href: "/music/audio-branding",
    Icon: Music2,
  },
  {
    label: "Series, Cine y TV",
    why: "Score original · sound design · supervisión musical · sync licensing",
    href: "/music/series-cine-tv",
    Icon: Film,
  },
  {
    label: "Producción Artística",
    why: "Composición · arreglo · grabación · mezcla · masterización",
    href: "/music/produccion-artistica",
    Icon: Mic2,
  },
];

export default function MusicPage() {
  const { content, loading } = useContent();
  if (loading || !content) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white/60 font-mono-caps tracking-widest">CARGANDO...</div>;
  }

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
      <Header />

      {/* HERO — Así suena Kilombo Music */}
      <section className="relative min-h-[55vh] flex items-center pt-32 pb-4 md:pt-40 md:pb-6 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#ff6a2b]/20 via-black/70 to-black" />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 text-center w-full">
          <p className="font-mono-caps text-xs md:text-sm tracking-[0.3em] uppercase text-[#ff8a3c]">Kilombo Music</p>
          <h1 className="mt-6 font-display font-black text-[36px] leading-[1.05] sm:text-5xl md:text-6xl lg:text-[72px] lg:leading-[1.02]">
            Así <span className="text-gradient-music">suena</span> Kilombo Music
          </h1>
        </div>
      </section>

      {/* CINEMATIC REEL — solo video */}
      <CinematicReel
        vimeoId="1221284837"
        eyebrow=""
      />

      {/* NUESTROS SERVICIOS */}
      <section id="servicios" className="relative bg-neutral-950 py-20 md:py-28 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display font-black text-4xl sm:text-5xl md:text-5xl lg:text-6xl leading-[1.05] max-w-5xl" style={{ wordBreak: "keep-all", overflowWrap: "normal" }}>
            <span className="text-gradient-music">Nuestros servicios</span>
          </h2>

          <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
            {SERVICE_CARDS.map(({ label, why, href, Icon }) => (
              <Link
                key={href}
                to={href}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-neutral-950 hover:border-[#ff8a3c]/50 transition-colors"
              >
                <div className="p-6 md:p-7 border-b border-white/10">
                  <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-white/40">El cómo</p>
                  <div className="mt-5 flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#ffa957] to-[#ff6a2b] flex items-center justify-center shadow-lg shadow-[#ff6a2b]/20 shrink-0">
                      <Icon className="w-5 h-5 text-black" />
                    </span>
                    <h3 className="font-display font-bold text-xl lg:text-2xl text-white leading-tight">{label}</h3>
                  </div>
                </div>
                <div className="p-6 md:p-7 flex-1 flex flex-col bg-gradient-to-br from-[#ff6a2b]/10 via-transparent to-transparent">
                  <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-white/40">El porqué</p>
                  <p className="mt-4 text-[#ffb46b] font-display font-bold text-base md:text-lg leading-snug">
                    {why}
                  </p>
                  <span className="mt-auto pt-6 inline-flex items-center gap-2 font-mono-caps uppercase tracking-[0.15em] text-xs text-white/70 group-hover:text-[#ff8a3c] transition-colors">
                    Descubre más <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-black py-20 md:py-28 px-6">
        <div className="max-w-4xl mx-auto text-center rounded-3xl border border-[#ff6a2b]/30 bg-gradient-to-br from-[#ff6a2b]/12 via-black to-black p-8 md:p-16">
          <h2 className="font-display font-black text-[26px] sm:text-3xl md:text-4xl lg:text-5xl leading-tight text-white" style={{ wordBreak: "keep-all", overflowWrap: "normal" }}>
            ¿Y si te reconocieran <span className="text-gradient-music">sólo por cómo suenas</span>?
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-xl mx-auto">
            Diseñamos el sonido de marcas, historias y artistas — para que se queden en la memoria.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-shine inline-flex items-center gap-3 rounded-full bg-white text-black pl-6 pr-5 py-3.5 font-mono-caps text-xs tracking-[0.2em] uppercase hover:bg-neutral-100 transition-colors">
              Quiero empezar a sonar
              <span className="w-7 h-7 rounded-full bg-[#ff6a2b] text-black flex items-center justify-center"><ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          </div>
        </div>
      </section>

      <Footer footer={content.footer} />
      <WhatsAppButton />
    </div>
  );
}
